import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";

import { RadioGroup } from "@headlessui/react";
import BreadCrumb from "components/lib/Global/BreadCrumb";
import RadioCards from "components/lib/Global/RadioCards";
import { Constants } from "config/constants";
import { Roles_Permissions_Constants } from "config/roles-permissions";
import { COMPANY_PROCESSES } from "config/tabs";
import CompanyDashboardContext from "context/CompanyDashboardContext";
import { ManageModuleContext } from "context/ManageCompanyModuleContext";
import { resetActiveDashboardSlice } from "state/slices/activeDashboard";
import { resetBatchNumber } from "state/slices/batchNumber";
import { getEmployeeAsync } from "state/slices/employee";
import { getAllInitiateTransactionChangeAsync } from "state/slices/initiateTransactionChange";
import { setCookie } from "utils/cookie";
import Changes from "./Changes";
import Corrections from "./Corrections";
import Draft from "./Draft";
import Review from "./Review";
import Signature from "./Signature";
import UpdateCompanyDetails from "./UpdateCompanyDetails";
import AnnualCompliance from "./AnnualCompliance";

const MaintainYourCompany = (props) => {
  const location = useLocation();
  const { companyId } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  useEffect(() => {
    if (companyId) {
      dispatch(getEmployeeAsync({ companyId: companyId }));
    }
  }, [dispatch, companyId]);

  const {
    dashboardRoles,
    dashboardPermissions,
    designation,
    companyOfficials,
    company,
  } = useSelector((state) => state.companyDashboardSlice);

  const currentUser = useSelector((state) => state.userSlice);
  const { getEmployee } = useSelector((state) => state.employeeSlice);

  const { initiatedTransactions } = useSelector(
    (state) => state.initiatedTransactionsSlice
  );
  const [changeOfParticularsInfo, setChangeOfParticularsInfo] = useState({
    selectedValueType: "",
  });
  const [selectedCompanyProcess, setSelectedCompanyProcess] = useState(null);

  const {
    setSelectedManageModuleTab,
    selectedManageModuleTab,
    resetSelectedChangesData,
  } = useContext(CompanyDashboardContext);
  const [currentStateOfProcess, setCurrentStateOfProcess] = useState(
    selectedManageModuleTab ? selectedManageModuleTab : "default"
  );
  const [transactionId, setTransactionId] = useState(null); // Store transaction ID
  const [transactionPayload, setTransactionPayload] = useState({
    officials: [],
    changeOfParticularDate: null,
    applicationType: null,
    status: "DRAFT",
    companyId: null,
    changeType: "CHANGE-OF-PARTICULARS",
    trx: null,
  }); // Single transaction payload for change of particulars

  const [appointmentStatus, setAppointmentStatus] = useState(false);
  const [isManageCompanyForm, setIsManageCompanyForm] = useState(null);
  const pages = [
    { name: "Home", href: "#", current: false },
    { name: "Maintain a company", href: "#", current: true },
  ];

  const hash = location.hash.slice(1);

  const isDirectorReviewOrApprove = dashboardRoles.some(
    (role) =>
      role.name === Roles_Permissions_Constants.DASHBOARD_ROLES.REVIEWER &&
      [
        Constants.APPLICANT_CAPACITIES.DIRECTOR,
        Constants.APPLICANT_CAPACITIES.DIRECTOR_SHAREHOLDER,
        Constants.APPLICANT_CAPACITIES.ALTERNATE_DIRECTOR,
      ].includes(designation)
  );

  const isReviewer = dashboardRoles.some(
    (role) => role.name === Roles_Permissions_Constants.DASHBOARD_ROLES.REVIEWER
  );

  const isLegalDepartment = getEmployee?.items.some(
    (employee) =>
      employee?.isLegalDepartment && employee?.user?._id === currentUser._id
  );

  const isRegistrar = dashboardRoles.some(
    (role) =>
      role.name === Roles_Permissions_Constants.DASHBOARD_ROLES.REGISTRAR
  );

  const isUploader = dashboardRoles.some(
    (role) => role.name === Roles_Permissions_Constants.DASHBOARD_ROLES.UPLOADER
  );

  const isStrikeOff =
    company?.status === Constants.ENTITY_STATUS.STRUCK_OFF ||
    company?.status === Constants.ENTITY_STATUS.STRIKE_OFF_ONGOING;

  const showReviewCardOnStrikeOff = (transaction) => {
    if (isStrikeOff) {
      return (
        [
          Constants.MAINTAIN_COMPANY_REVIEW_PROCESS.DIRECTOR_APPROVAL,
          Constants.MAINTAIN_COMPANY_REVIEW_PROCESS.SHAREHOLDER_APPROVAL,
          Constants.MAINTAIN_COMPANY_REVIEW_PROCESS.REGISTRAR_APPROVAL,
          Constants.MAINTAIN_COMPANY_REVIEW_PROCESS.LEGAL_APPROVAL,
          Constants.MAINTAIN_COMPANY_REVIEW_PROCESS.REGISTRAR_REVIEW,
        ].includes(transaction.status) &&
        [
          Constants.TRANSACTION_CHANGE_TYPES.ADMINISTRATIVE_RESTORATION,
          Constants.TRANSACTION_CHANGE_TYPES.NOTICE_OF_WITHDRAWAL_OF_STRIKE_OFF,
          Constants.TRANSACTION_CHANGE_TYPES.STRIKE_OFF,
        ].includes(transaction.changeType)
      );
    } else {
      return true;
    }
  };

  const showDraftCardOnStrikeOff = (transaction) => {
    return (
      [Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.DRAFT].includes(
        transaction.status
      ) &&
      [
        Constants.TRANSACTION_CHANGE_TYPES.ADMINISTRATIVE_RESTORATION,
        Constants.TRANSACTION_CHANGE_TYPES.NOTICE_OF_WITHDRAWAL_OF_STRIKE_OFF,
        Constants.TRANSACTION_CHANGE_TYPES.STRIKE_OFF,
      ].includes(transaction.changeType)
    );
  };

  const transactionStatuses = {
    reviewApplication: (transaction) => {
      const isUnderReview = Object.values(
        Constants.MAINTAIN_COMPANY_REVIEW_PROCESS
      ).includes(transaction.status);
      if (isUnderReview) {
        return (
          ((isDirectorReviewOrApprove &&
            [
              Constants.MAINTAIN_COMPANY_REVIEW_PROCESS.DIRECTOR_APPROVAL,
              Constants.MAINTAIN_COMPANY_REVIEW_PROCESS.SHAREHOLDER_APPROVAL,
              Constants.MAINTAIN_COMPANY_REVIEW_PROCESS.REGISTRAR_APPROVAL,
            ].includes(transaction.status)) ||
            isRegistrar ||
            (isLegalDepartment &&
              [
                Constants.MAINTAIN_COMPANY_REVIEW_PROCESS.LEGAL_APPROVAL,
              ].includes(transaction.status))) &&
          showReviewCardOnStrikeOff(transaction)
        );
      }
      return false;
    },
    signatureApplication: (transaction) => {
      const isUserESignatureRequired =
        transaction.changeType &&
        Object.values(Constants.MAINTAIN_COMPANY_SIGNATURE_STATUS).includes(
          transaction.status
        ) &&
        transaction?.envelopes?.some((envelope) =>
          envelope?.signatures?.some(
            (signer) => signer?.signer_email_address === currentUser.email
          )
        );

      if (isUserESignatureRequired) {
        return true;
      }
      return (
        (isUploader || isRegistrar) &&
        transaction.changeType &&
        Object.values(Constants.MAINTAIN_COMPANY_SIGNATURE_STATUS).includes(
          transaction.status
        )
      );
    },

    draftApplication: (transaction) => {
      const isDraft =
        transaction.changeType &&
        transaction.status === Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.DRAFT;

      if (!isDraft) return false;

      if (isStrikeOff) {
        return (
          showDraftCardOnStrikeOff(transaction) && (isRegistrar || isUploader)
        );
      } else {
        if (isRegistrar || isUploader) {
          return true;
        }

        return transaction.createdBy === currentUser._id;
      }
    },

    correctionApplication: (transaction) =>
      transaction.changeType &&
      Object.values(Constants.MAINTAIN_COMPANY_CORRECTIONS_STATUSES).includes(
        transaction.status
      ),
  };

  const [companyProcesses, setCompanyProcesses] = useState([]);

  useEffect(() => {
    setCompanyProcesses(
      Object.values(COMPANY_PROCESSES).filter((card) => {
        if (transactionStatuses[card.id]) {
          return initiatedTransactions.items.some(transactionStatuses[card.id]);
        }
        return true;
      })
    );
  }, [initiatedTransactions.items]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const subTab = queryParams.get("subTab");

    if (hash) {
      setSelectedCompanyProcess(
        companyProcesses.find((companyProcess) => companyProcess.hash === hash)
      );
      setCurrentStateOfProcess(hash);
    } else if (subTab) {
      setCurrentStateOfProcess(subTab);
    }
  }, [location, companyProcesses, hash]);

  const handleExitDashboard = () => {
    setCookie("role", Constants.COOKIE_ROLES.APPLICANT);
    dispatch(resetActiveDashboardSlice());
    history.push("/companies");
  };

  const handleCompanyProcessSelection = (companyProcess) => {
    if (companyProcess.hash) {
      setSelectedManageModuleTab(companyProcess.hash);
      resetSelectedChangesData();
      setCurrentStateOfProcess(companyProcess.hash);
    }
  };

  const handleBackToManageYourCompanyDashboard = () => {
    dispatch(
      getAllInitiateTransactionChangeAsync({
        companyId,
        params: {
          limit: 10,
          skip: 0,
          search: "",
          sort: "updatedAt",
          order: "desc",
        },
      })
    );
    setCurrentStateOfProcess("default");
  };

  useEffect(() => {
    dispatch(
      getAllInitiateTransactionChangeAsync({
        companyId,
        params: {
          limit: null,
          skip: 0,
          search: "",
          sort: "updatedAt",
          order: "desc",
        },
      })
    );
  }, []);

  const handleGoToDraft = () => {
    setCurrentStateOfProcess("draft-application");
  };

  useEffect(() => {
    dispatch(resetBatchNumber());
    if (hash) {
      setSelectedCompanyProcess(
        companyProcesses.find((companyProcess) => companyProcess.hash === hash)
      );
    }
  }, [hash, companyProcesses, currentStateOfProcess]);

  const filteredCompanyProcesses = companyProcesses.filter((process) => {
    if (process.id === "updateCompanyDetails") {
      return isUploader || isRegistrar;
    } else if (process.id === "annualCompliance") {
      return !isStrikeOff;
    } else if (process.id === "changes") {
      return !isStrikeOff;
    } else {
      return true;
    }
  });

  return (
    <ManageModuleContext.Provider
      value={{
        currentStateOfProcess,
        appointmentStatus,
        setAppointmentStatus,
        appointmentInfo: {
          idType: Constants.ID_TYPES.KENYAN_CITIZEN,
        },
        cessationInfo: {},
        isManageCompanyForm,
        setIsManageCompanyForm,
        handleBackToManageYourCompanyDashboard,
        handleGoToDraft,
        changeOfParticularsInfo,
        setChangeOfParticularsInfo,
        transactionId,
        setTransactionId,
        transactionPayload,
        setTransactionPayload,
      }}
    >
      <div className="container mx-auto px-4">
        {/* Default State */}
        {currentStateOfProcess === "default" && (
          <div className=" text-sflPrimary">
            <div className="bg-white container mx-auto px-4">
              <div className="mb-6">
                <BreadCrumb
                  pages={pages}
                  handleExitDashboard={handleExitDashboard}
                />
              </div>
            </div>
            <RadioGroup
              value={selectedCompanyProcess}
              onChange={handleCompanyProcessSelection}
              className="my-4"
            >
              <RadioGroup.Label className="text-base">
                <div>
                  <h2 className="pb-4 font-semibold text-title5">
                    What you would like to do today
                  </h2>
                  <h1 className="font-medium">
                    Changes section, here you can update your company details or
                    information if you want
                  </h1>
                </div>
              </RadioGroup.Label>
              <RadioCards options={filteredCompanyProcesses} />
            </RadioGroup>
          </div>
        )}
        {currentStateOfProcess !== "default" ? (
          <div>
            {/* Company Changes State */}
            {currentStateOfProcess === "company-changes" && <Changes />}

            {/* Annual Returns State */}
            {currentStateOfProcess === "annual-compliance" && (
              <AnnualCompliance />
            )}

            {/* Review Application State */}
            {currentStateOfProcess === "review-application" && <Review />}

            {/* Signature Application State */}
            {currentStateOfProcess === "signature-application" && <Signature />}

            {/* Draft Application State */}
            {currentStateOfProcess === "draft-application" && (
              <Draft setCurrentStateOfProcess={setCurrentStateOfProcess} />
            )}

            {/* Correction Application State */}
            {currentStateOfProcess === "correction-application" && (
              <Corrections />
            )}

            {
              /** Update company details */
              currentStateOfProcess === "update-company-details" && (
                <UpdateCompanyDetails />
              )
            }
          </div>
        ) : null}
      </div>
    </ManageModuleContext.Provider>
  );
};

export default MaintainYourCompany;
